// Read-only Prelead detail drawer for the staff monitor — full record + NDA/CIM
// gate state + activity timeline + (when escalated) the back-link into the Leads
// pipeline. Phoenix is the Loan Administrator; the "listing broker" fields are
// the third-party source of the business-for-sale listing, never Phoenix.

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Link2,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, Skeleton, Textarea } from "@/design-system/components/ui";
import { preleadsApi } from "@/api/leadGen/preleads";
import {
  useAddPreleadNote,
  usePrelead,
  usePreleadActivity,
  usePreleadAttachments,
  usePreleadEarnings,
  usePreleadNotes,
  useSourcePreleadForLead,
} from "@/hooks/leadGen/usePreleads";
import { useDateFormatter } from "@/utils/datetime";
import {
  displayEmail,
  formatCurrency,
  formatPhone,
  NO_EMAIL_ON_FILE,
} from "@/design-system/lib/format";
import type {
  PreleadInterestStatus,
  PreleadOutreachStatus,
} from "@/api/leadGen/preleads";
import {
  CIM_STATUS_LABEL,
  EARNING_STATE_BADGE,
  INTEREST_BADGE,
  NDA_STATUS_LABEL,
  PRELEAD_SOURCE_LABEL,
  PRELEAD_STATUS_BADGE,
  PRELEAD_STATUS_LABEL,
} from "./leadGenShared";
import { PreleadStaffActions } from "./PreleadStaffActions";

const MONEY = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** State-string → friendly label (the wire carries "PendingReview"). */
const EARNING_STATE_LABEL: Record<string, string> = {
  Estimated: "Estimated",
  PendingReview: "Pending",
  Approved: "Approved",
  Adjusted: "Adjusted",
};

/** One pertinent link the generator captured off the source listing. */
interface CapturedLink {
  href: string;
  label?: string | null;
}

/** Parse the stored `capturedLinksJson` defensively → only well-formed http(s)
 *  links (mirrors the portal console's parser). Malformed JSON → []. */
function parseCapturedLinks(json: string | null | undefined): CapturedLink[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (l): l is CapturedLink =>
        typeof l?.href === "string" && /^https?:\/\//i.test(l.href),
    );
  } catch {
    return [];
  }
}

/** Parse the stored `aiFitFlagsJson` (a JSON array of advisory poor-fit strings)
 *  defensively → a clean string[]. Malformed / non-array → []. */
function parseFitFlags(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.filter((f): f is string => typeof f === "string" && f.trim().length > 0);
  } catch {
    return [];
  }
}

/** Compact human file size — no shared formatter exists in @/design-system/lib/format. */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The green "confirmed" milestone chips fed back from the escalated Lead —
 *  shown only when the matching timestamp is set. Same visual style as the
 *  other milestone badges (green success token). Shared by the list row + this
 *  drawer so the two stay in lockstep. */
export function ConfirmedBadges({
  confirmedContactedAtUtc,
  confirmedInterestedAtUtc,
}: {
  confirmedContactedAtUtc?: string | null;
  confirmedInterestedAtUtc?: string | null;
}) {
  if (!confirmedContactedAtUtc && !confirmedInterestedAtUtc) return null;
  return (
    <>
      {confirmedContactedAtUtc && (
        <Badge status="finalized" className="gap-space-1">
          <CheckCircle2 size={11} strokeWidth={2} aria-hidden /> Confirmed contacted
        </Badge>
      )}
      {confirmedInterestedAtUtc && (
        <Badge status="finalized" className="gap-space-1">
          <CheckCircle2 size={11} strokeWidth={2} aria-hidden /> Confirmed interested
        </Badge>
      )}
    </>
  );
}

const OUTREACH_LABEL: Record<PreleadOutreachStatus, string> = {
  None: "Not contacted",
  MessageSent: "Message sent",
  ResponseReceived: "Response received",
};

const INTEREST_STATUS_LABEL: Record<PreleadInterestStatus, string> = {
  None: "—",
  Interested: "Interested",
};

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="text-small text-onyx break-words">{value || <span className="text-gray-mid">—</span>}</div>
    </div>
  );
}

export function PreleadDetailDrawer({
  preleadId,
  onClose,
  showStaffActions = true,
  sourceOfLeadId,
}: {
  preleadId: string;
  onClose: () => void;
  /** The dashboard mounts the staff Disqualify/Archive/Delete actions in the
   *  header; the Lead-page source-prelead view (Area A) passes false for a purely
   *  read-only look. */
  showStaffActions?: boolean;
  /** Area A — when set, this drawer is the source-prelead view opened from a Lead.
   *  The detail is fetched via the Leads.Read endpoint (GET /leads/{id}/source-prelead)
   *  so a lead handler without LeadGen.View can read it, and the LeadGen.View-only
   *  sections (earnings / note thread / activity / attachments) are hidden. */
  sourceOfLeadId?: string;
}) {
  const { date, dateTime } = useDateFormatter();
  const isLeadMode = !!sourceOfLeadId;
  // Only ONE detail source is enabled at a time (the other passes null → disabled).
  const staffDetail = usePrelead(isLeadMode ? null : preleadId);
  const leadDetail = useSourcePreleadForLead(sourceOfLeadId ?? null);
  const detail = isLeadMode ? leadDetail : staffDetail;
  // The LeadGen.View-gated sub-resources are disabled (null id) in lead mode.
  const activity = usePreleadActivity(isLeadMode ? null : preleadId);
  const notes = usePreleadNotes(isLeadMode ? null : preleadId);
  const earnings = usePreleadEarnings(isLeadMode ? null : preleadId);
  const attachments = usePreleadAttachments(isLeadMode ? null : preleadId);
  const addNote = useAddPreleadNote(preleadId);
  const staffActions = showStaffActions && !isLeadMode;
  const [noteBody, setNoteBody] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const p = detail.data;

  const contactName = p
    ? [p.contactFirstName, p.contactLastName].filter(Boolean).join(" ")
    : "";
  const capturedLinks = parseCapturedLinks(p?.capturedLinksJson);
  const aiFitFlags = parseFitFlags(p?.aiFitFlagsJson);

  // Fetch a fresh short-lived SAS on demand and open it (read-only — no download
  // is embedded in the list). Staff are not generator-scoped.
  async function handleDownload(docId: string) {
    setDownloadError(null);
    setDownloadingId(docId);
    try {
      const dl = await preleadsApi.attachmentDownload(preleadId, docId);
      window.open(dl.sasUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDownloadError((err as Error).message || "Couldn't open that file.");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={p?.businessName || contactName || "Prelead"}
      description="Read-only view — the generator drives this record from their portal console."
    >
      {detail.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : detail.isError || !p ? (
        <p className="text-small text-status-error" role="alert">
          {detail.isError ? (detail.error as Error).message : "Prelead not found."}
        </p>
      ) : (
        <div className="flex flex-col gap-space-5">
          <div className="flex flex-wrap items-center gap-space-2">
            <Badge status={PRELEAD_STATUS_BADGE[p.status]}>{PRELEAD_STATUS_LABEL[p.status]}</Badge>
            <Badge status={INTEREST_BADGE[p.interestLevel]}>{p.interestLevel}</Badge>
            <ConfirmedBadges
              confirmedContactedAtUtc={p.confirmedContactedAtUtc}
              confirmedInterestedAtUtc={p.confirmedInterestedAtUtc}
            />
            {p.isArchived && <Badge status="archived">Archived</Badge>}
            {p.escalatedToLeadId && (
              <Link
                to={`/leads/${p.escalatedToLeadId}`}
                className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:underline"
              >
                <ArrowUpRight size={14} strokeWidth={1.5} aria-hidden /> View escalated lead
              </Link>
            )}
            {staffActions && (
              <div className="ml-auto">
                <PreleadStaffActions
                  prelead={{
                    id: p.id,
                    status: p.status,
                    isArchived: p.isArchived,
                    displayName: p.businessName || contactName || "Prelead",
                  }}
                  onDone={onClose}
                />
              </div>
            )}
          </div>

          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Business &amp; contact
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-space-4">
              <Field label="Business" value={p.businessName} />
              <Field label="Contact" value={contactName} />
              <Field label="Title" value={p.title} />
              <Field label="Phone" value={formatPhone(p.phone)} />
              <Field label="Email" value={displayEmail(p.email) ?? (p.email ? NO_EMAIL_ON_FILE : "")} />
              <Field label="Industry" value={p.industry} />
              <Field
                label="Location"
                value={[p.city, p.state, p.zip].filter(Boolean).join(", ")}
              />
              <Field label="Asking price" value={p.askingPrice != null ? formatCurrency(p.askingPrice) : ""} />
              <Field label="Cash flow" value={p.cashFlow != null ? formatCurrency(p.cashFlow) : ""} />
            </div>
          </section>

          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Source
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-space-4">
              <Field label="Source" value={PRELEAD_SOURCE_LABEL[p.source]} />
              <Field label="Source detail" value={p.sourceDetail} />
              <Field
                label="Listing URL"
                value={
                  p.listingUrl ? (
                    <a
                      href={p.listingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-space-1 text-gold-shadow hover:underline break-all"
                    >
                      {p.listingUrl} <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
                    </a>
                  ) : null
                }
              />
              <Field label="Listing broker (source)" value={p.brokerName} />
              <Field label="Listing broker company" value={p.brokerCompany} />
              <Field label="Broker email" value={displayEmail(p.brokerEmail) ?? ""} />
              <Field label="Broker phone" value={formatPhone(p.brokerPhone)} />
            </div>
          </section>

          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Links &amp; files
            </h3>

            <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
              Captured links
            </div>
            {capturedLinks.length === 0 ? (
              <p className="text-small text-gray-mid m-0 mb-space-3">No links captured.</p>
            ) : (
              <ul className="flex flex-wrap gap-space-2 list-none p-0 m-0 mb-space-3">
                {capturedLinks.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noreferrer"
                      title={l.href}
                      className="inline-flex max-w-full items-center gap-space-1 rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-onyx hover:border-gold"
                    >
                      <Link2 size={12} strokeWidth={1.5} className="shrink-0 text-gold-shadow" aria-hidden />
                      <span className="truncate">{l.label || l.href}</span>
                      <ExternalLink size={11} strokeWidth={1.5} className="shrink-0 text-gray-mid" aria-hidden />
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {!isLeadMode && (
              <>
            <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
              Attachments
            </div>
            {attachments.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : attachments.isError ? (
              <p className="text-small text-status-error m-0" role="alert">
                {(attachments.error as Error).message}
              </p>
            ) : !attachments.data || attachments.data.length === 0 ? (
              <p className="text-small text-gray-mid m-0">No files attached.</p>
            ) : (
              <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
                {attachments.data.map((f) => (
                  <li
                    key={f.docId}
                    className="flex items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-pure p-space-2"
                  >
                    <div className="flex min-w-0 items-center gap-space-2">
                      <FileText size={14} strokeWidth={1.5} className="shrink-0 text-gray-mid" aria-hidden />
                      <span className="text-small text-onyx break-words">{f.fileName}</span>
                      <span className="shrink-0 text-caption text-gray-mid tabular-nums">
                        {formatFileSize(f.sizeBytes)}
                      </span>
                    </div>
                    <Button
                      variant="text"
                      size="sm"
                      className="shrink-0"
                      loading={downloadingId === f.docId}
                      onClick={() => handleDownload(f.docId)}
                    >
                      <Download size={14} strokeWidth={1.5} aria-hidden /> Download
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {downloadError && (
              <p className="text-caption text-status-error m-0 mt-space-2" role="alert">
                {downloadError}
              </p>
            )}
              </>
            )}
          </section>

          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Milestone tracks
            </h3>
            <p className="text-caption text-gray-mid mb-space-2">
              Read-only — the generator advances these four independent tracks from their console.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-space-4">
              <Field label="Outreach" value={OUTREACH_LABEL[p.outreachStatus]} />
              <Field label="NDA" value={NDA_STATUS_LABEL[p.ndaStatus]} />
              <Field label="CIM" value={CIM_STATUS_LABEL[p.cimStatus]} />
              <Field label="Interest" value={INTEREST_STATUS_LABEL[p.interestStatus]} />
              <Field label="Message sent" value={p.messageSentAtUtc ? date(p.messageSentAtUtc) : ""} />
              <Field
                label="Response received"
                value={p.responseReceivedAtUtc ? date(p.responseReceivedAtUtc) : ""}
              />
              <Field label="NDA completed" value={p.ndaCompletedAtUtc ? date(p.ndaCompletedAtUtc) : ""} />
              <Field label="CIM received" value={p.cimReceivedAtUtc ? date(p.cimReceivedAtUtc) : ""} />
            </div>
          </section>

          {!isLeadMode && (
          <section>
            <div className="flex items-center justify-between gap-space-3 mb-space-2">
              <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">
                Earnings
              </h3>
              {earnings.data && (
                <span className="text-small font-semibold tabular-nums text-status-success">
                  {formatCurrency(earnings.data.total, MONEY)} total
                </span>
              )}
            </div>
            {earnings.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : earnings.isError ? (
              <p className="text-small text-status-error" role="alert">
                {(earnings.error as Error).message}
              </p>
            ) : !earnings.data || earnings.data.lineItems.length === 0 ? (
              <p className="text-small text-gray-mid">No credits yet.</p>
            ) : (
              <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
                {earnings.data.lineItems.map((line, i) => (
                  <li
                    key={`${line.kpiCode}-${line.state}-${i}`}
                    className="flex items-center justify-between gap-space-3"
                  >
                    <div className="flex min-w-0 items-center gap-space-2">
                      <span className="text-small text-onyx break-words">{line.label}</span>
                      <Badge status={EARNING_STATE_BADGE[line.state as keyof typeof EARNING_STATE_BADGE] ?? "draft"}>
                        {EARNING_STATE_LABEL[line.state] ?? line.state}
                      </Badge>
                    </div>
                    <span className="text-small font-semibold tabular-nums text-onyx">
                      {formatCurrency(line.amount, MONEY)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {(p.aiSummary || aiFitFlags.length > 0 || p.rawPastedText) && (
            <section className="flex flex-col gap-space-3">
              <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">
                Raw paste &amp; AI extract
              </h3>
              {p.aiSummary && (
                <div>
                  <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
                    AI summary
                    {p.aiModel ? ` · ${p.aiModel}` : ""}
                    {p.aiExtractedAtUtc ? ` · ${date(p.aiExtractedAtUtc)}` : ""}
                  </div>
                  <p className="text-small text-onyx whitespace-pre-wrap m-0">{p.aiSummary}</p>
                </div>
              )}
              {aiFitFlags.length > 0 && (
                <div>
                  <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
                    AI fit flags <span className="normal-case">(advisory — never blocks)</span>
                  </div>
                  <ul className="flex flex-wrap gap-space-2 list-none p-0 m-0">
                    {aiFitFlags.map((flag) => (
                      <li key={flag}>
                        <Badge status="review">{flag}</Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {p.rawPastedText && (
                <details className="rounded-md border border-border-light bg-white-warm p-space-3">
                  <summary className="cursor-pointer text-small font-semibold text-onyx">
                    Raw pasted text
                  </summary>
                  <pre className="mt-space-2 max-h-96 overflow-auto whitespace-pre-wrap break-words text-caption text-onyx m-0 font-mono">
                    {p.rawPastedText}
                  </pre>
                </details>
              )}
            </section>
          )}

          {(!isLeadMode || p.notes) && (
          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Notes
            </h3>
            {p.notes && (
              <div className="rounded-md border border-border-light bg-white-warm p-space-3 mb-space-2">
                <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">
                  Record note
                </div>
                <p className="text-small text-onyx whitespace-pre-wrap m-0">{p.notes}</p>
              </div>
            )}
            {!isLeadMode && (
              <>
            {notes.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : !notes.data || notes.data.length === 0 ? (
              <p className="text-small text-gray-mid">No notes yet.</p>
            ) : (
              <ul className="flex flex-col gap-space-2 list-none p-0 m-0 mb-space-3">
                {notes.data.map((n) => (
                  <li
                    key={n.id}
                    className="rounded-md border border-border-light bg-white-pure p-space-3"
                  >
                    <div className="flex items-center gap-space-2 mb-space-1">
                      <Badge status={n.source === "AI" ? "info" : "draft"}>
                        {n.source === "AI" ? "AI" : "Note"}
                      </Badge>
                      <span className="text-caption text-gray-mid">{dateTime(n.createdAtUtc)}</span>
                    </div>
                    <p className="text-small text-onyx whitespace-pre-wrap m-0">{n.body}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col gap-space-2">
              <Textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                rows={2}
                placeholder="Add a note…"
                aria-label="Add a note"
              />
              {addNote.isError && (
                <p className="text-caption text-status-error m-0" role="alert">
                  {(addNote.error as Error).message}
                </p>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="self-end"
                loading={addNote.isPending}
                disabled={noteBody.trim().length === 0}
                onClick={() =>
                  addNote.mutate(noteBody.trim(), { onSuccess: () => setNoteBody("") })
                }
              >
                Add note
              </Button>
            </div>
              </>
            )}
          </section>
          )}

          {!isLeadMode && (
          <section>
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-2">
              Activity
            </h3>
            {activity.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : !activity.data || activity.data.length === 0 ? (
              <p className="text-small text-gray-mid">No activity recorded yet.</p>
            ) : (
              <ol className="flex flex-col gap-space-2 list-none p-0 m-0">
                {activity.data.map((a) => (
                  <li key={a.id} className="flex items-start gap-space-3 border-l-2 border-border-light pl-space-3">
                    <div className="min-w-0">
                      <div className="text-small text-onyx">{a.description}</div>
                      <div className="text-caption text-gray-mid">{dateTime(a.timestamp)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
          )}

          <div className="text-caption text-gray-mid">
            Created {dateTime(p.createdAt)}
            {p.modifiedAt ? ` · Updated ${dateTime(p.modifiedAt)}` : ""}
          </div>
        </div>
      )}
    </Modal>
  );
}
